import {
  Button,
  Collapse,
  Typography,
  IconButton,
} from '@mui/material';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useState } from 'react';
import chatApi from './chat.api';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { landingPageClasses } from '../utility/constants';
import sg_dash from '../../images/sg_dash_16x16.png'

export default function ChatSidebar({
  farms = [],
  activeFarm,
  sessions = [],
  activeSessionId,
  onFarmChange,
  onSessionChange,
  onSessionCreated,
  onSessionDelete,
  userId,
  onNewChat,
  isDark,
  currDarkThemeClass,
}) {
  const [openChats, setOpenChats] = useState(true);
  const [openFarms, setOpenFarms] = useState(false);
  const navigate = useNavigate();

  /* ---------------- Delete Chat ---------------- */
  const handleDeleteChat = async (e, sessionId) => {
    e.stopPropagation();
    if (!activeFarm?.id || !userId || !sessionId) return;

    onSessionDelete(sessionId); // optimistic

    try {
      await chatApi.clearChat({
        farm_id: String(activeFarm.id),
        user_id: String(userId),
        session_id: String(sessionId),
      });
    } catch (err) {
      console.error('Delete chat failed', err);
    }
  };

  return (
    <Box
      sx={{
        width: 320,
        height: '100vh',
        borderRight: '1px solid #2E2F2D',
        display: 'flex',
        flexDirection: 'column',
        background: '#2a3a2f',
      }}
    >
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          gap: .5,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        <Box
          component="img"
          src={sg_dash}
          alt="SmartGrow"
          sx={{
            height: 24,
            width: 24,
          }}
        />

        <Typography
          sx={{
            color: 'white',
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: 14,
            mt: 1,
            userSelect: 'none',
          }}
        >
          SMARTGROW
        </Typography>
      </Box>

      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Button variant="contained" fullWidth onClick={onNewChat}>
          New Chat
        </Button>
      </Box>

      {/* 🧾 RECENT CHATS (SCROLLABLE) */}
      <Box sx={{ px: 2, flex: 1, minHeight: 0 }}>
        <Box
          onClick={() => setOpenChats(p => !p)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'white',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">Recent Chats</Typography>
          <ExpandMoreIcon
            sx={{
              transform: openChats ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: '0.2s',
            }}
          />
        </Box>

        <Collapse in={openChats} style={{ height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              overflowY: 'auto',
              maxHeight: '100%',
              pr: 1,
            }}
          >
            {sessions.length === 0 && (
              <Typography variant="body2" color="#A1A591">
                No chats yet
              </Typography>
            )}

            {sessions.map(session => (
              <Box
                key={session.session_id}
                onClick={() => onSessionChange(session.session_id)}
                sx={{
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  color: 'white',
                  bgcolor:
                    activeSessionId === session.session_id
                      ? '#4a5d45'
                      : 'transparent',
                  '&:hover': { bgcolor: '#3f513a' },
                }}
              >
                <Typography variant="body2" noWrap>
                  {session.session_title || 'Chat Session'}
                </Typography>

                <IconButton
                  size="small"
                  onClick={e =>
                    handleDeleteChat(e, session.session_id)
                  }
                  sx={{ color: '#A1A591' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Box
          onClick={() => setOpenFarms(p => !p)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'white',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">
            Farms
            {activeFarm?.farm_name && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 0.5, opacity: 0.7 }}
              >
                ({activeFarm.license_number})
              </Typography>
            )}
          </Typography>

          <ExpandMoreIcon
            sx={{
              transform: openFarms ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: '0.2s',
            }}
          />
        </Box>

        <Collapse in={openFarms}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {farms.map(farm => (
              <Box
                key={farm.id}
                onClick={() => onFarmChange(farm)}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  color: 'white',
                  bgcolor:
                    activeFarm?.id === farm.id
                      ? '#4a5d45'
                      : 'transparent',
                  '&:hover': { bgcolor: '#3f513a' },
                }}
              >
                <Typography variant="body2">
                  {farm.farm_name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
